import { useState, useEffect } from 'react';
import { useParams, useOutletContext, Link } from 'react-router-dom';
import { Trophy, TrendingUp, Users, Zap, Star, BarChart2, ChevronRight } from 'lucide-react';
import api from '../services/api.js';
import { useTeamRole } from '../hooks/useTeamRole.js';
// 2026-07-10 (batch-B E6) — logged-in user, for the "my results" strip.
import { useAuth } from '../context/AuthContext.jsx';
import toast from 'react-hot-toast';
import { PageSpinner } from '../components/Skeleton.jsx';
import { format, parseISO } from 'date-fns';
import ExportMenu from '../components/ExportMenu.jsx';
// 2026-07-27 — expandable per-player mini-game detail (the receipts
// behind each leaderboard row). Shared with MyStatsPage.
import PlayerGameLog from '../components/PlayerGameLog.jsx';

// v2.5.149 — AI Log display cleaner. The response_summary stored for
// some AI calls is the raw model output, which can be:
//   • a markdown-fenced blob: ```json\n{...}\n```
//   • a bare rating-suggestion object: {"suggested_rating":2,"reason":"..."}
//   • a truncated fence fragment: ```json   (model output got cut)
//   • normal prose (season summaries) — leave as-is
// This returns:
//   • a readable string for known shapes,
//   • '' (empty) when there's a row but no meaningful text (caller
//     still renders the header so the coach sees the interaction
//     happened),
//   • null when the entry is pure noise (just a fence fragment) and
//     shouldn't render at all.
function renderAiSummary(raw) {
  if (raw == null) return '';
  let s = String(raw).trim();
  if (!s) return '';

  // Strip surrounding markdown code fences (```json ... ``` or ``` ... ```).
  // Also handles a lone opening fence with no close (truncated output).
  const fenceStripped = s
    .replace(/^```[a-zA-Z]*\s*/, '')   // opening fence + optional lang
    .replace(/```$/, '')                // closing fence
    .trim();

  // If after stripping there's nothing left (it was just "```json" or
  // "```json{"), treat as noise — don't render the entry.
  if (
    fenceStripped === '' ||
    fenceStripped === '{' ||
    (/^```/.test(s) && fenceStripped.length < 3)
  ) {
    return null;
  }

  // Try to parse a rating-suggestion object and render it readably.
  // Tolerate trailing junk by extracting the first {...} block.
  const objMatch = fenceStripped.match(/\{[\s\S]*\}/);
  if (objMatch) {
    try {
      const obj = JSON.parse(objMatch[0]);
      if (obj && (obj.suggested_rating != null || obj.reason)) {
        const rating = obj.suggested_rating != null ? `Suggested rating: ${obj.suggested_rating}` : null;
        const reason = obj.reason ? String(obj.reason) : null;
        return [rating, reason].filter(Boolean).join(' — ') || fenceStripped;
      }
    } catch {
      // Not valid JSON — fall through to showing the cleaned text.
    }
  }

  // Normal prose (season summaries etc.) — return the fence-stripped text.
  return fenceStripped;
}

export default function StatsPage() {
  const { teamId }               = useParams();
  const { currentSeasonId = null } = useOutletContext() || {}; // v2.5.189
  const seasonParam = currentSeasonId ? currentSeasonId : 'all';
  const { isCoach, loading: roleLoading } = useTeamRole(teamId);
  const { user } = useAuth(); // E6 — for the personal results strip
  const [stats, setStats]        = useState(null);
  const [feedback, setFeedback]  = useState([]);
  const [aiLog, setAiLog]        = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading]    = useState(true);
  // 2026-07-10 (batch-B E5, coach request) — Overview tab REMOVED and
  // Leaderboard is the default for everyone (the Apr-2026 coach default
  // of Player Log is superseded by the same request). Default leaderboard
  // sort = most wins.
  const [tab, setTab]            = useState('leaderboard');
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [lbSort, setLbSort]      = useState('wins'); // 'wins' | 'plusMinus' | 'gamesIn' | 'noShows'
  // 2026-09-17 (per Gilles) — leaderboard roster filter: everyone /
  // full-time only / spares only. Frontend-only (rows carry isFullTime).
  const [lbType, setLbType]      = useState('all');  // 'all' | 'ft' | 'spare'
  // 2026-07-25 (per Gilles) — goalie stat section (separate from the
  // skater leaderboard; W/L + GF/GA attribution honors the team's
  // goalie_swap_each_mini setting server-side).
  const [goalieStats, setGoalieStats] = useState(null); // { swapEnabled, goalies: [] }
  // 2026-07-27 — which leaderboard/goalie row is expanded to show the
  // game-by-game detail (null = none; stores the player's user id).
  const [expandedPlayerId, setExpandedPlayerId] = useState(null);
  // 2026-07-27 — the raw reconciliation CSV export moved to the
  // super-admin area (Admin → Teams → Raw Stats Export); the coach-
  // facing button + endpoint were removed per Gilles.

  // Fix #47: isCoach is loaded asynchronously by useTeamRole. The previous
  // useEffect only depended on teamId, so loadAll always ran while isCoach
  // was still false — coaches never got the AI log on first load.
  // Now we wait for roleLoading to finish before running loadAll, and include
  // isCoach in deps so a role change triggers a fresh fetch.
  useEffect(() => {
    if (!roleLoading) loadAll();
  }, [teamId, isCoach, roleLoading, currentSeasonId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statsRes, fbRes] = await Promise.all([
        api.get(`/teams/${teamId}/stats`, { params: { seasonId: seasonParam } }),
        api.get(`/teams/${teamId}/stats/feedback`),
      ]);
      setStats(statsRes.data);
      setFeedback(fbRes.data);

      const lbRes = await api.get(`/teams/${teamId}/stats/leaderboard`, { params: { seasonId: seasonParam } }).catch(() => ({ data: [] }));
      setLeaderboard(lbRes.data);

      // 2026-07-25 — goalie section. .catch(null) so a pre-deploy
      // backend (endpoint missing → 404) just hides the section.
      const gRes = await api.get(`/teams/${teamId}/stats/goalies`, { params: { seasonId: seasonParam } }).catch(() => ({ data: null }));
      setGoalieStats(gRes.data);

      // 2026-07-10 (batch-B A7) — AI-log fetch removed with its tab
      // (backend endpoint left in place per house rule).
    } catch { toast.error('Failed to load stats'); }
    setLoading(false);
  };

  const [seasonSummary, setSeasonSummary] = useState(null);

  // 2026-07-10 (batch-B F1) — AI rating analysis (coach-only, advisory).
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const runAiAnalysis = async () => {
    setAiAnalysisLoading(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/stats/ai/analysis`);
      if (!data.analysis) {
        toast(data.message || '🤖 AI not configured — add an API key in Admin → AI Settings', { duration: 5000 });
      } else {
        setAiAnalysis(data.analysis);
        toast.success(`🤖 Analyzed ${data.playersAnalyzed} player${data.playersAnalyzed !== 1 ? 's' : ''}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'AI analysis failed');
    }
    setAiAnalysisLoading(false);
  };

  // 2026-07-25 (per Gilles) — hide players who've never played (and
  // never even RSVPed in) from the leaderboard. Keeps the list to
  // people who actually show up; the gamesIn fallback protects teams
  // that RSVP but haven't recorded scores/rosters yet from rendering
  // an empty leaderboard.
  // 2026-07-27 (per Gilles) — goalies (full-time AND spare, e.g. a
  // spare goalie like the reported Alex Sirin case) no longer appear
  // in the skater leaderboard at all: their home is the 🥅 Goalies
  // section below, where W/L + GF/GA follow goalie attribution rules
  // (incl. the swap-each-mini setting). Skater ranks therefore no
  // longer shift when a goalie is added.
  const visibleLeaderboard = leaderboard.filter(
    p => p.position !== 'goalie' &&
         ((p.gamesPlayed || 0) > 0 || (p.gamesIn || 0) > 0)
  );


  const requestSeasonSummary = async () => {
    setAiAnalyzing(true);
    setSeasonSummary(null);
    try {
      const { data } = await api.post(`/teams/${teamId}/stats/ai/season-summary`);
      if (!data.summary) {
        toast('🤖 AI not configured — add an API key to enable season summaries', { duration: 4000 });
        setAiAnalyzing(false);
        return;
      }
      const text = data.summary || data.content;
      setSeasonSummary(text);
      toast.success('🤖 Season summary ready!', { duration: 3000 });
    } catch { toast.error('AI summary failed — check your AI provider config'); }
    setAiAnalyzing(false);
  };

  if (loading || roleLoading) return (
    <PageSpinner />
  );

  const s = stats || {};
  const gamesPlayed = (s.completedGames || 0);
  // Backend computes winRate directly; fall back to 0 if no games with results
  const winRate = s.winRate != null ? s.winRate : 0;
  // Fix #57: include both 'great_balance' and 'about_right' as positive outcomes
  // (the label already says 'great/ok' — the calculation should match)
  const avgBalance = feedback.length > 0
    ? (feedback.filter(f => ['great_balance','about_right'].includes(f.feedback_type)).length / feedback.length * 100).toFixed(0)
    : null;

  // Balance score trend (last 10 games with feedback)
  const recentFeedback = [...feedback]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 10)
    .reverse();

  return (
    <div className="animate-fade-in">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="section-title text-4xl">Stats & History</h1>
          <p className="text-secondary text-sm mt-1">
            {/* Apr 30 2026 (App_Change_List #15) — scope clarifier.
                Stats default to the active season per the new
                seasons model; without this label a coach who just
                activated a new season would see all zeros and
                worry that stats broke. The label only appears when
                the team actually has a named active season — pre-
                seasons teams continue to see the generic subtitle. */}
            {stats?.seasonScope?.name
              ? <>Showing <span style={{ color: 'var(--ice)', fontWeight: 600 }}>{stats.seasonScope.name}</span> · Team performance and balance tracking</>
              : stats?.seasonScope?.id === 'all'
                ? <>Showing all seasons · Team performance and balance tracking</>
                : <>Team performance and balance tracking</>}
          </p>
        </div>
        {/* 2026-07-10 (batch-B A7) — AI Season Summary button + result
            card removed per coach request (handler kept as dead code;
            the backend endpoint remains). */}
      </div>

      {/* Stat cards
          2026-07-10 (batch-B E4, per Gilles) — "Games Played" is now the
          SCHEDULED count (calendar games only), with the mini-games
          count shown underneath when the team tracks them. The old
          value mixed scheduled + mini rows. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Games Played"
          value={Number(s.scheduledGamesPlayed ?? gamesPlayed) || 0}
          icon={<Trophy size={18} />} color="var(--ice)"
          sub={Number(s.miniGamesPlayed) > 0 ? `+ ${Number(s.miniGamesPlayed)} mini-games tracked` : 'scheduled games'} />
        {/* 2026-09-17 (per Gilles) — "Players" and "Win Rate" tiles replaced
            by one record card per SIDE. (The old Win Rate was the share of
            scored games that weren't ties — meaningless for a pickup team
            that plays itself; it read ~100% by construction.) Labels come
            from the team's own home/away names when set (Black/White…),
            else Home/Away. */}
        {[s.sides?.home || { label: 'Home' }, s.sides?.away || { label: 'Away' }].map((side, i) => {
          const gp = (side.wins || 0) + (side.losses || 0) + (side.ties || 0);
          return (
            <div key={i} className="glass rounded-xl p-4"
              title={`${side.label}: ${side.wins || 0} wins, ${side.losses || 0} losses${(side.ties || 0) ? `, ${side.ties} ties` : ''} across ${gp} scored game${gp === 1 ? '' : 's'} (mini-games count individually)`}>
              <div className="flex items-center gap-2 mb-2" style={{ color: i === 0 ? 'var(--goal)' : 'var(--puck)' }}>
                {i === 0 ? <Trophy size={18} /> : <Zap size={18} />}
                <span className="text-xs font-semibold uppercase tracking-wide text-secondary truncate">{side.label}</span>
              </div>
              <div className="section-title text-3xl" style={{ color: i === 0 ? 'var(--goal)' : 'var(--puck)' }}>
                {gp > 0 ? `${side.wins || 0}-${side.losses || 0}${(side.ties || 0) > 0 ? `-${side.ties}` : ''}` : '—'}
              </div>
              <div className="text-xs text-secondary mt-1">
                {gp > 0
                  ? <>GF <strong style={{ color: 'var(--text)' }}>{side.goalsFor || 0}</strong> · GA <strong style={{ color: 'var(--text)' }}>{side.goalsAgainst || 0}</strong>{(side.ties || 0) > 0 ? ' · W-L-T' : ' · W-L'}</>
                  : 'no scored games yet'}
              </div>
            </div>
          );
        })}
        {/* 2026-09-17 — tooltip explains the number (Gilles: "always seems
            to be 100%"). It's the share of post-game feedback answers that
            said the teams were balanced — so with no negative answers it IS
            100%, not a bug. */}
        <div className="glass rounded-xl p-4"
          title={`Balance Score = post-game feedback answers of "Great balance" or "About right" ÷ all feedback answers. ${feedback.length} answer${feedback.length === 1 ? '' : 's'} so far${avgBalance ? ` — ${feedback.filter(f => ['great_balance','about_right'].includes(f.feedback_type)).length} said balanced` : ''}. 100% simply means nobody has said a game was lopsided yet.`}
          style={{ cursor: 'help' }}>
          <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--ice)' }}>
            <BarChart2 size={18} />
            <span className="text-xs font-semibold uppercase tracking-wide text-secondary">Balance Score</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-3)', border: '1px solid var(--border)', borderRadius: 999, width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>?</span>
          </div>
          <div className="section-title text-3xl" style={{ color: 'var(--ice)' }}>{avgBalance ? `${avgBalance}%` : '—'}</div>
          <div className="text-xs text-secondary mt-1">
            {avgBalance ? `${feedback.length} feedback answer${feedback.length === 1 ? '' : 's'} said "balanced"` : 'no feedback yet'}
          </div>
        </div>
      </div>

      {/* 2026-07-10 (batch-B E6, per Gilles) — MY RESULTS strip: stats
          from the logged-in player's perspective (their leaderboard row:
          attendance, mini-game W-L-T from THEIR team assignments, GF/GA,
          +/-). Rendered for everyone with a row — coaches who play too. */}
      {(() => {
        const me = (leaderboard || []).find(p => p.id === user?.id);
        if (!me) return null;
        const cell = (label, val, color) => (
          <div style={{ textAlign: 'center', minWidth: 64 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: color || 'var(--text)' }}>{val}</div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</div>
          </div>
        );
        return (
          <div className="card mb-6" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', border: '1px solid var(--ice-a20)' }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ice)', margin: 0 }}>Your results</p>
              <p className="text-xs text-secondary" style={{ margin: '2px 0 0' }}>Mini-game record from the games you played</p>
              {/* 2026-09-17 (per Gilles) — coaches who play get the same
                  full tile set as any player: link to the My Stats page. */}
              <Link to={`/team/${teamId}/my-stats`} className="text-xs" style={{ color: 'var(--ice)', fontWeight: 700, display: 'inline-block', marginTop: 4 }}>
                View my full stats →
              </Link>
            </div>
            {/* 2026-09-17 — "Showed up" → "Checked in"; the separate "Played"
                cell is gone (Checked in already counts the games played,
                including the coach's own). */}
            {cell('Checked in', me.gamesIn ?? 0)}
            {cell('Record', `${me.wins ?? 0}-${me.losses ?? 0}${(me.ties ?? 0) > 0 ? `-${me.ties}` : ''}`, 'var(--goal)')}
            {cell('GF · GA', `${me.goalsFor ?? 0} · ${me.goalsAgainst ?? 0}`)}
            {cell('+/−', `${(me.plusMinus ?? 0) > 0 ? '+' : ''}${me.plusMinus ?? 0}`,
              (me.plusMinus ?? 0) > 0 ? 'var(--goal)' : (me.plusMinus ?? 0) < 0 ? 'var(--penalty, #f87171)' : undefined)}
            {(me.noShows ?? 0) > 0 && cell('👎 No-shows', me.noShows, 'var(--puck)')}
          </div>
        );
      })()}

      {/* Tab nav */}
      {/* Mobile fix: for coaches this row has 3 tabs (Leaderboard/Player
          Log/AI Analysis) whose combined width sits right at the edge of
          a ~390px viewport with no overflow wrapper — flexWrap lets it
          drop to a second line instead of forcing horizontal scroll on
          the whole page body. */}
      <div className="flex gap-1 mb-5 p-1 rounded-xl w-fit"
        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {/* 2026-07-10 (batch-B A7 + E5) — Overview, AI Log and Spare
            Plays tabs removed (their render bodies remain below as
            unreachable dead code per house rule). Balance History gets
            replaced by AI Analysis in phase F. */}
        {[
          ['leaderboard', '🏆 Leaderboard'],
          ...(isCoach ? [['player-log', '📇 Player Log']] : []),
          // 2026-07-10 (batch-B F1, per Gilles) — Balance History
          // replaced by coach-only AI Analysis (its body remains below
          // as dead code per house rule).
          ...(isCoach ? [['ai-analysis', '🤖 AI Analysis']] : []),
        ].map(([val, label]) => (
          <button key={val} onClick={() => setTab(val)}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{
              background: tab === val ? 'var(--ice-a12)' : 'transparent',
              color: tab === val ? 'var(--ice)' : 'var(--text-secondary)',
            }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Overview tab ── */}
      {tab === 'overview' && (
        <div className="space-y-4">
          {/* Season breakdown */}
          <div className="card">
            <h2 className="font-bold mb-4">Season Breakdown</h2>
            <div className="space-y-3">
              {[
                { label: 'Completed games', val: s.completedGames || 0, total: s.totalGames || 0, color: 'var(--goal)' },
                { label: 'Upcoming games',  val: s.upcomingGames || 0,  total: s.totalGames || 0, color: 'var(--ice)' },
                { label: 'Games with rosters', val: s.gamesWithRosters || 0, total: s.completedGames || 0, color: 'var(--puck)' },
              ].map(row => (
                <div key={row.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-secondary">{row.label}</span>
                    <span style={{ color: row.color }}>{row.val}</span>
                  </div>
                  {row.total > 0 && (
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(100, (row.val / row.total) * 100)}%`, background: row.color }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Top players by games played (coach only) */}
          {isCoach && s.topPlayers?.length > 0 && (
            <div className="card">
              <h2 className="font-bold mb-4">Most Active Players</h2>
              <div className="space-y-2">
                {s.topPlayers.slice(0, 8).map((p, i) => (
                  <div key={p.userId} className="flex items-center gap-3 py-1.5">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{
                        background: i < 3 ? 'var(--ice-a15)' : 'var(--surface-2)',
                        color: i < 3 ? 'var(--ice)' : 'var(--text-secondary)',
                      }}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{p.name}</div>
                      <div className="text-xs text-secondary">{p.gamesIn} games in · {p.gamesOut} out</div>
                    </div>
                    <span className={`badge rating-${p.rating}`}>{p.rating || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Leaderboard tab ── */}
      {/* 2026-07-10 (batch-B F1) — AI ANALYSIS (coach-only, advisory).
          Reviews mini-game W/L + GF/GA per player against their rating
          and recommends promotions/demotions. Provider/model set in
          Admin → AI Settings (Anthropic + Sonnet per Gilles); token/cost
          tracking rides the existing ai_usage_log like other features. */}
      {tab === 'ai-analysis' && isCoach && (
        <div className="card">
          <div className="flex items-start justify-between mb-3" style={{ gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h3 className="font-semibold" style={{ margin: 0 }}>🤖 Rating Analysis</h3>
              <p className="text-xs text-secondary" style={{ margin: '4px 0 0' }}>
                AI reviews each player's mini-game record against their rating and suggests who might
                deserve a bump up or down. Advisory only — nothing changes unless you re-rate them.
              </p>
            </div>
            <button onClick={runAiAnalysis} disabled={aiAnalysisLoading} className="btn-ice text-sm" style={{ flexShrink: 0 }}>
              {aiAnalysisLoading
                ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Analyzing…</>
                : <><Zap size={14} /> {aiAnalysis ? 'Re-run analysis' : 'Run analysis'}</>}
            </button>
          </div>
          {aiAnalysis
            ? <p className="text-sm text-secondary leading-relaxed whitespace-pre-line">{aiAnalysis}</p>
            : !aiAnalysisLoading && (
              <p className="text-sm" style={{ color: 'var(--text-3)' }}>
                Run the analysis after a few scored mini-games — the more games with saved scores and
                rosters, the better the recommendations.
              </p>
            )}
        </div>
      )}

      {tab === 'leaderboard' && (
        <div className="space-y-4">
          {/* Sort controls + Export */}
          <div className="flex gap-2 flex-wrap items-center">
            {/* 2026-07-27 (per Gilles) — chip order: Wins, +/-, Games,
                In (default sort is Most Wins). */}
            {/* 2026-09-17 (per Gilles) — order: Wins → Best +/− → Most
                checked in → No-shows. "Most Games" chip dropped. The
                No-shows view lists ONLY flagged players. */}
            {[
              { key: 'wins',       label: '🏆 Wins'            },
              { key: 'plusMinus',  label: '➕ Best +/−'        },
              { key: 'gamesIn',    label: '✅ Most checked in' },
              { key: 'noShows',    label: '👎 No-shows'        },
            ].map(s => (
              <button key={s.key} onClick={() => setLbSort(s.key)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: lbSort === s.key ? 'var(--ice-a12)' : 'var(--surface-2)',
                  border: `1px solid ${lbSort === s.key ? 'var(--ice-a30)' : 'var(--border)'}`,
                  color: lbSort === s.key ? 'var(--ice)' : 'var(--text-secondary)',
                }}>
                {s.label}
              </button>
            ))}
            {/* v2.5.153 — Export the leaderboard in its current sort.
                Includes the W-L-T columns we surfaced in v2.5.149. */}
            <div style={{ marginLeft: 'auto' }}>
              <ExportMenu
                filename="leaderboard"
                title={`Leaderboard — ${lbSort === 'wins' ? 'Wins' : lbSort === 'plusMinus' ? 'Best +/−' : lbSort === 'noShows' ? 'No-shows' : 'Most checked in'}`}
                columns={[
                  { key: 'rank',    label: '#',       align: 'right' },
                  { key: 'name',    label: 'Player' },
                  { key: 'role',    label: 'Role' },
                  { key: 'rating',  label: 'Rating',  align: 'center' },
                  { key: 'in',      label: 'In',      align: 'right' },
                  { key: 'wins',    label: 'Wins',    align: 'right' },
                  { key: 'losses',  label: 'Losses',  align: 'right' },
                  { key: 'ties',    label: 'Ties',    align: 'right' },
                  { key: 'winPct',  label: 'Win %',   align: 'right' },
                  { key: 'played',  label: 'Played',  align: 'right' },
                  { key: 'gf',      label: 'GF',      align: 'right' },
                  { key: 'ga',      label: 'GA',      align: 'right' },
                  { key: 'pm',      label: '+/-',     align: 'right' },
                  { key: 'noShows', label: 'No-shows', align: 'right' },
                ]}
                getRows={() => {
                  // Mirror the on-screen sort exactly so the file matches
                  // (incl. the 2026-07-25 zero-game filter).
                  const sorted = [...visibleLeaderboard].sort((a, b) => (b[lbSort] || 0) - (a[lbSort] || 0));
                  return sorted.map((p, i) => ({
                    rank:   i + 1,
                    name:   p.name,
                    role:   p.role === 'coach' ? 'Coach' :
                            p.position === 'goalie' ? 'Goalie' :
                            p.isFullTime === false ? 'Spare' : 'Full-Time',
                    rating: p.rating ?? '—',
                    in:     p.gamesIn || 0,
                    wins:   p.wins || 0,
                    losses: p.losses || 0,
                    ties:   p.ties || 0,
                    winPct: (() => {
                      const dec = (p.wins || 0) + (p.losses || 0) + (p.ties || 0);
                      return dec > 0 ? `${Math.round(((p.wins || 0) / dec) * 100)}%` : '—';
                    })(),
                    played: p.gamesPlayed || 0,
                    gf:     p.goalsFor || 0,
                    ga:     p.goalsAgainst || 0,
                    pm:     ((p.plusMinus || 0) > 0 ? '+' : '') + (p.plusMinus || 0),
                    noShows: p.noShows || 0,
                  }));
                }}
              />
            </div>
          </div>

          {/* 2026-09-17 — Full-time / Spares filter */}
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-xs text-secondary">Show:</span>
            {[
              { key: 'all',   label: 'Everyone' },
              { key: 'ft',    label: 'Full-time' },
              { key: 'spare', label: 'Spares' },
            ].map(t => (
              <button key={t.key} onClick={() => setLbType(t.key)}
                className="px-3 py-1 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: lbType === t.key ? 'var(--ice-a12)' : 'var(--surface-2)',
                  border: `1px solid ${lbType === t.key ? 'var(--ice-a30)' : 'var(--border)'}`,
                  color: lbType === t.key ? 'var(--ice)' : 'var(--text-secondary)',
                }}>
                {t.label}
              </button>
            ))}
          </div>
          {(() => {
            // 2026-09-17 — apply the type filter, then the sort. No-shows
            // view only lists players with at least one flag.
            const rowsToShow = visibleLeaderboard
              .filter(p => lbType === 'all' ? true : lbType === 'ft' ? p.isFullTime === true : p.isFullTime === false)
              .filter(p => lbSort === 'noShows' ? (p.noShows || 0) > 0 : true)
              .sort((a, b) => (b[lbSort] || 0) - (a[lbSort] || 0));
            const isHidden = (p, key) => Array.isArray(p.hidden) && p.hidden.includes(key);
            return rowsToShow.length === 0 ? (
            <div className="glass p-12 text-center text-secondary">
              <Trophy size={48} className="mx-auto mb-3 opacity-20" />
              <p>{lbSort === 'noShows'
                ? '🎉 No no-shows this season — everyone who checked in showed up.'
                : lbType !== 'all'
                  ? 'No players match this filter yet.'
                  : 'No player data yet — start tracking availability!'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {rowsToShow
                .map((p, i) => {
                  const RATING_COLOR = { 1: '#00ff88', 2: '#00d4ff', 3: '#ff9500', 4: '#ff4444' };
                  const rc = RATING_COLOR[p.rating] || 'var(--text-secondary)';
                  const attendance = (p.gamesIn || 0) + (p.gamesOut || 0) + (p.gamesMaybe || 0);
                  const pct = attendance > 0 ? Math.round((p.gamesIn || 0) / attendance * 100) : 0;
                  // 2026-09-17 — the No-shows view gets unhappy faces instead
                  // of medals (per Gilles: "make it fun"); worst offender first.
                  const NO_SHOW_FACES = ['😾', '☹️', '😬'];
                  const medal = lbSort === 'noShows'
                    ? (NO_SHOW_FACES[i] || '👎')
                    : (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null);
                  // 2026-09-17 — stat privacy: a player who hid a tile shows a
                  // lock in that column for everyone but themselves.
                  const LOCK = <span title="This player keeps this stat private">🔒</span>;
                  const attendanceHidden = isHidden(p, 'attendance');
                  return (
                    // 2026-07-14 (mobile pass) — added flex-wrap. This row packs
                    // rank + avatar + name/attendance + a 5-column stats block
                    // (In / W-L / Played / GF·GA / +/-) all on one line with no
                    // wrap; at ~390px the fixed-width stats block alone (5
                    // columns x ~16px gap) doesn't leave room for the name, so
                    // the row was overflowing the card. flex-wrap lets the stats
                    // block (w-full below sm: on the block itself) drop to its
                    // own line instead of clipping or forcing horizontal scroll.
                    // 2026-07-27 — rows are clickable: expand to the per-game
                    // detail (PlayerGameLog) that backs the numbers.
                    <div key={p.id} className="glass-hover rounded-xl p-4 flex items-center gap-4 flex-wrap"
                      onClick={() => setExpandedPlayerId(prev => prev === p.id ? null : p.id)}
                      title="Click to see this player's game-by-game results"
                      style={{ cursor: 'pointer' }}>
                      {/* Rank */}
                      <div className="w-8 text-center flex-shrink-0">
                        {medal
                          ? <span className="text-xl">{medal}</span>
                          : <span className="text-sm font-bold text-secondary">#{i+1}</span>}
                      </div>

                      {/* Avatar */}
                      <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
                        style={{ background: p.rating ? `${rc}18` : 'var(--surface-2)', border: `1px solid ${p.rating ? rc+'30' : 'var(--border)'}`, color: rc }}>
                        {p.name?.[0]?.toUpperCase() || '?'}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold truncate">{p.name}</span>
                          {p.position && <span className="text-xs text-secondary capitalize">{p.position}</span>}
                          {p.rating && <span className={`badge rating-${p.rating} text-xs`}>{['','Elite','Strong','Avg','Dev'][p.rating]}</span>}
                        </div>
                        {/* Attendance bar (hidden when the player keeps attendance private) */}
                        {!attendanceHidden && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: 'var(--goal)' }} />
                          </div>
                          <span className="text-xs text-secondary flex-shrink-0">{pct}% in</span>
                        </div>
                        )}
                      </div>

                      {/* Stats — v2.5.149 — Wins column expanded
                          to W-L-T triple. "In" now counts only past
                          mini-games (was counting all future RSVPs
                          too, producing inflated values like "15 In"
                          on a team with 4 played weeks). "Played" =
                          actual mini-game appearances. */}
                      <div className="flex gap-4 flex-shrink-0 text-center w-full sm:w-auto justify-around sm:justify-end mt-2 sm:mt-0">
                        <div>
                          <div className="text-base font-bold" style={{ color: 'var(--goal)', fontFamily: 'var(--font-display)' }}>{attendanceHidden ? LOCK : p.gamesIn}</div>
                          <div className="text-xs text-secondary">Checked in</div>
                        </div>
                        {/* 2026-09-17 — no-show count sits right beside
                            Checked in (thumbs-down); only when > 0. */}
                        {(p.noShows || 0) > 0 && (
                          <div title={`${p.noShows} no-show${p.noShows === 1 ? '' : 's'} — checked in but didn't show up`}>
                            <div className="text-base font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--puck)' }}>
                              {p.noShows}
                            </div>
                            <div className="text-xs text-secondary">👎 No-show</div>
                          </div>
                        )}
                        <div>
                          <div className="text-base font-bold" style={{ color: 'var(--puck)', fontFamily: 'var(--font-display)' }}>
                            {isHidden(p, 'record') ? LOCK : <>{p.wins}-{p.losses || 0}{p.ties > 0 ? `-${p.ties}` : ''}</>}
                          </div>
                          <div className="text-xs text-secondary">{p.ties > 0 ? 'W-L-T' : 'W-L'}</div>
                        </div>
                        {/* 2026-07-27 — Win % (decided games only: W/(W+L+T)).
                            Frontend-derived so no backend change; '—' until
                            the player has a decided result. */}
                        <div>
                          <div className="text-base font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--goal)' }}>
                            {(() => {
                              if (isHidden(p, 'record') || isHidden(p, 'winRate')) return LOCK;
                              const dec = (p.wins || 0) + (p.losses || 0) + (p.ties || 0);
                              return dec > 0 ? `${Math.round(((p.wins || 0) / dec) * 100)}%` : '—';
                            })()}
                          </div>
                          <div className="text-xs text-secondary">Win %</div>
                        </div>
                        <div>
                          <div className="text-base font-bold" style={{ color: 'var(--ice)', fontFamily: 'var(--font-display)' }}>{isHidden(p, 'gamesPlayed') ? LOCK : p.gamesPlayed}</div>
                          <div className="text-xs text-secondary">Played</div>
                        </div>
                        {/* v2.5.188 — mini-game goals for/against + plus-minus. */}
                        <div>
                          <div className="text-base font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text)' }}>
                            {isHidden(p, 'goals') ? LOCK : <>{p.goalsFor || 0}<span style={{ color: 'var(--text-3)', fontWeight: 400 }}>·</span>{p.goalsAgainst || 0}</>}
                          </div>
                          <div className="text-xs text-secondary">GF·GA</div>
                        </div>
                        <div>
                          <div className="text-base font-bold" style={{ fontFamily: 'var(--font-display)',
                            color: (p.plusMinus || 0) > 0 ? 'var(--goal)' : (p.plusMinus || 0) < 0 ? 'var(--penalty)' : 'var(--text-2)' }}>
                            {isHidden(p, 'plusMinus') ? LOCK : <>{(p.plusMinus || 0) > 0 ? '+' : ''}{p.plusMinus || 0}</>}
                          </div>
                          <div className="text-xs text-secondary">+/−</div>
                        </div>
                        {/* 2026-09-17 — the trailing 👻 cell moved up beside
                            "Checked in" as 👎 No-show. */}
                      </div>
                      {/* 2026-07-27 — expanded per-game detail. w-full drops it
                          onto its own line inside the flex-wrap row. */}
                      {expandedPlayerId === p.id && (
                        <div className="w-full" onClick={e => e.stopPropagation()}>
                          <PlayerGameLog teamId={teamId} userId={p.id} seasonParam={seasonParam} />
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          );
          })()}

          {/* ── 2026-07-25 — GOALIES section (per Gilles). Separate from
              the skater leaderboard: goalie W/L + GF/GA attribution
              honors the team's goalie_swap_each_mini setting (server-
              side) — mini 1 as assigned, sides swap each new mini. ── */}
          {goalieStats?.goalies?.length > 0 && (
            <div className="card">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-semibold" style={{ margin: 0 }}>🥅 Goalies</h3>
                {goalieStats.swapEnabled && (
                  <span className="text-xs px-2 py-0.5 rounded-md"
                    style={{ background: 'var(--ice-a10)', color: 'var(--ice)', border: '1px solid var(--ice-a20)' }}>
                    Sides swap each mini-game
                  </span>
                )}
              </div>
              <div className="space-y-2">
                {goalieStats.goalies.map((g, i) => (
                  <div key={g.id} className="glass-hover rounded-xl p-4 flex items-center gap-4 flex-wrap"
                    onClick={() => setExpandedPlayerId(prev => prev === g.id ? null : g.id)}
                    title="Click to see this goalie's game-by-game results"
                    style={{ cursor: 'pointer' }}>
                    <div className="w-8 text-center flex-shrink-0">
                      {i === 0 ? <span className="text-xl">🥇</span>
                        : <span className="text-sm font-bold text-secondary">#{i + 1}</span>}
                    </div>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
                      style={{ background: 'var(--ice-a12)', border: '1px solid var(--ice-a30)', color: 'var(--ice)' }}>
                      {g.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold truncate">{g.name}</span>
                      <div className="text-xs text-secondary mt-0.5">
                        {g.winPct !== null ? `${g.winPct}% win rate` : 'No scored games yet'}
                      </div>
                    </div>
                    <div className="flex gap-4 flex-shrink-0 text-center w-full sm:w-auto justify-around sm:justify-end mt-2 sm:mt-0">
                      <div>
                        <div className="text-base font-bold" style={{ color: 'var(--puck)', fontFamily: 'var(--font-display)' }}>
                          {g.wins == null ? '🔒' : <>{g.wins}-{g.losses}{g.ties > 0 ? `-${g.ties}` : ''}</>}
                        </div>
                        <div className="text-xs text-secondary">{g.ties > 0 ? 'W-L-T' : 'W-L'}</div>
                      </div>
                      <div>
                        <div className="text-base font-bold" style={{ color: 'var(--ice)', fontFamily: 'var(--font-display)' }}>{g.gamesPlayed == null ? '🔒' : g.gamesPlayed}</div>
                        <div className="text-xs text-secondary">Played</div>
                      </div>
                      <div>
                        <div className="text-base font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text)' }}>
                          {g.goalsFor == null ? '🔒' : <>{g.goalsFor}<span style={{ color: 'var(--text-3)', fontWeight: 400 }}>·</span>{g.goalsAgainst}</>}
                        </div>
                        <div className="text-xs text-secondary">GF·GA</div>
                      </div>
                      <div>
                        <div className="text-base font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--goal)' }}>
                          {g.gaa !== null ? g.gaa : '—'}
                        </div>
                        <div className="text-xs text-secondary">GA/game</div>
                      </div>
                    </div>
                    {/* 2026-07-27 — expanded per-game detail (swap-aware). */}
                    {expandedPlayerId === g.id && (
                      <div className="w-full" onClick={e => e.stopPropagation()}>
                        <PlayerGameLog teamId={teamId} userId={g.id} seasonParam={seasonParam} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Balance feedback tab ── */}
      {tab === 'feedback' && (
        <div className="space-y-3">
          {feedback.length === 0 ? (
            <div className="glass p-12 text-center text-secondary">
              <BarChart2 size={48} className="mx-auto mb-3 opacity-20" />
              <p>No balance feedback yet.</p>
              <p className="text-sm mt-1 opacity-60">After games, use the 🏆 Great / 👍 OK / ⚡ Uneven buttons on game pages.</p>
            </div>
          ) : (
            <>
              {/* Enhanced balance trend chart */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-bold text-sm">Balance Trend (last 10 games)</h2>
                  {avgBalance !== null && (
                    <span className="text-xs text-secondary">
                      {avgBalance}% great/ok
                    </span>
                  )}
                </div>
                <div className="relative">
                  {/* Chart bars */}
                  <div className="flex items-end gap-1.5 h-20">
                    {recentFeedback.map((fb, i) => {
                      const score = fb.feedback_type === 'great_balance' ? 100
                                  : fb.feedback_type === 'about_right'   ? 60
                                  : 20;
                      const col = fb.feedback_type === 'great_balance' ? 'var(--goal)'
                                : fb.feedback_type === 'about_right'   ? 'var(--ice)'
                                : 'var(--penalty)';
                      const label = fb.feedback_type === 'great_balance' ? '🏆 Great'
                                  : fb.feedback_type === 'about_right'   ? '👍 OK'
                                  : '⚡ Uneven';
                      const dateStr = fb.game_date ? format(parseISO(fb.game_date), 'MMM d') : '';
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                          <div className="rounded-t-sm transition-all w-full"
                            style={{ height: `${score * 0.8}%`, minHeight: 4, background: col, opacity: 0.85 }} />
                          {/* Tooltip */}
                          <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block z-10
                            bg-black/90 text-white text-xs rounded px-2 py-1 whitespace-nowrap pointer-events-none"
                            style={{ border: `1px solid ${col}40` }}>
                            {label}{dateStr ? ` · ${dateStr}` : ''}
                          </div>
                        </div>
                      );
                    })}
                    {recentFeedback.length === 0 && (
                      <p className="text-secondary text-sm w-full text-center self-center">No data yet</p>
                    )}
                  </div>
                  {/* Date labels */}
                  {recentFeedback.length > 0 && (
                    <div className="flex gap-1.5 mt-1.5">
                      {recentFeedback.map((fb, i) => (
                        <div key={i} className="flex-1 text-center text-xs text-secondary truncate" style={{ fontSize: 9 }}>
                          {fb.game_date ? format(parseISO(fb.game_date), 'M/d') : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-3 mt-3">
                  {[['var(--goal)','Great'],['var(--ice)','OK'],['var(--penalty)','Uneven']].map(([c,l]) => (
                    <div key={l} className="flex items-center gap-1.5 text-xs text-secondary">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ background: c }} />{l}
                    </div>
                  ))}
                </div>
              </div>

              {/* Feedback list */}
              {feedback.map(fb => (
                <div key={fb.id} className="glass rounded-xl p-4 flex items-center gap-4">
                  <div className="text-2xl">
                    {fb.feedback_type === 'great_balance' ? '🏆'
                     : fb.feedback_type === 'about_right' ? '👍' : '⚡'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm capitalize">{fb.feedback_type.replace(/_/g,' ')}</div>
                    <div className="text-xs text-secondary mt-0.5">
                      {fb.game_date ? format(parseISO(fb.game_date), 'MMM d, yyyy') : 'Unknown game'}
                      {fb.home_team_rating_sum && (
                        <> · Home: {fb.home_team_rating_sum} vs Away: {fb.away_team_rating_sum}</>
                      )}
                    </div>
                    {fb.notes && <p className="text-xs mt-1 text-secondary">{fb.notes}</p>}
                  </div>
                  {fb.ai_suggestion && (
                    <div className="text-xs max-w-48 text-secondary italic">
                      🤖 {fb.ai_suggestion}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* ── AI log tab (coaches only) ── */}
      {tab === 'ai' && isCoach && (
        <div className="space-y-3">
          {aiLog.length === 0 ? (
            <div className="glass p-12 text-center text-secondary">
              <Zap size={48} className="mx-auto mb-3 opacity-20" />
              <p>No AI interactions yet.</p>
              <p className="text-sm mt-1 opacity-60">
                AI interactions appear here when you use AI features on game pages.
              </p>
            </div>
          ) : aiLog.map(entry => {
            // v2.5.149 — clean up the AI Log display. Coach reported
            // "strange things": some entries showed raw ```json code-
            // fence fragments or {"suggested_rating": N, "reason":
            // "..."} blobs verbatim. Those are rating-suggestion calls
            // whose raw model output landed in response_summary. We
            // now: (1) strip markdown code fences, (2) if the payload
            // parses as a rating-suggestion object, render it as a
            // readable line, (3) hide entries that are just an empty
            // fence fragment with nothing useful. Falls back to the
            // raw text for normal summaries (e.g. season summaries).
            const summary = renderAiSummary(entry.response_summary);
            if (summary === null) return null; // nothing meaningful to show
            return (
              <div key={entry.id} className="glass rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Zap size={14} style={{ color: 'var(--ice)' }} />
                  <span className="text-xs font-semibold capitalize text-ice">
                    {entry.action_type?.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs text-secondary ml-auto">
                    {format(parseISO(entry.created_at), 'MMM d, h:mm a')}
                  </span>
                  <span className="badge text-xs" style={{ background: 'var(--surface-3)', color: 'var(--text-secondary)' }}>
                    {entry.provider}
                  </span>
                </div>
                {summary && (
                  <p className="text-sm text-secondary leading-relaxed">{summary}</p>
                )}
                {entry.tokens_used > 0 && (
                  <p className="text-xs text-secondary mt-2 opacity-50">{entry.tokens_used} tokens</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'spare-invoice' && isCoach && (
        <SpareInvoiceTab teamId={teamId} />
      )}
      {tab === 'player-log' && isCoach && (
        <PlayerLogTab teamId={teamId} seasonParam={seasonParam} />
      )}
    </div>
  );
}

function StatCard({ label, value, icon, color, sub }) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2" style={{ color }}>
        {icon}
        <span className="text-xs font-semibold uppercase tracking-wide text-secondary">{label}</span>
      </div>
      <div className="section-title text-3xl" style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-secondary mt-1">{sub}</div>}
    </div>
  );
}

// ── Spare Plays / Invoice tab ─────────────────────────────────────────────────
function SpareInvoiceTab({ teamId }) {
  const [plays,    setPlays]    = useState(null);
  // v2.5.149 — backend now wraps the array in { scope, plays } so we
  // can show the user which season they're looking at + tell them
  // when the active-season filter was bypassed (fell back to all
  // seasons because the active-season slice was empty). Track scope
  // separately so the loading + empty branches can render the hint.
  const [scope,    setScope]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    api.get(`/teams/${teamId}/games/spare-plays`)
      .then(r => {
        // Tolerate either the new {scope, plays} shape OR the old
        // bare-array shape so a mid-deploy cache doesn't break the
        // page. New shape dominates once the backend's caught up.
        if (Array.isArray(r.data)) {
          setPlays(r.data);
          setScope(null);
        } else {
          setPlays(r.data?.plays || []);
          setScope(r.data?.scope || null);
        }
      })
      .catch(() => toast.error('Failed to load spare plays'))
      .finally(() => setLoading(false));
  }, [teamId]);

  const updatePayment = async (play, status) => {
    const key = play.gameId;
    setUpdating(p => ({ ...p, [key]: true }));
    try {
      await api.patch(`/teams/${teamId}/games/spare-plays/${play.playId}`, { paymentStatus: status });
      // Optimistic update
      setPlays(prev => prev.map(p => ({
        ...p,
        plays: p.plays.map(pl => pl.gameId === play.gameId ? { ...pl, status } : pl),
      })));
      toast.success('Payment status updated');
    } catch { toast.error('Failed to update'); }
    setUpdating(p => ({ ...p, [key]: false }));
  };

  if (loading) return <div className="text-secondary text-sm">Loading spare plays…</div>;

  if (!plays || plays.length === 0) {
    // v2.5.149 — the backend already auto-falls-back to all-seasons
    // when the active-season slice is empty (see games.js /spare-plays).
    // So reaching here means there are genuinely NO spare plays in ANY
    // season — there's nothing a "check all seasons" link could reveal.
    // Show the plain track-&-invoice hint in all cases. (Earlier this
    // rendered a ?spareScope=all link that nothing read — dead link.)
    return (
      <div className="glass p-12 text-center text-secondary">
        <span style={{ fontSize: 40 }}>📋</span>
        <p className="mt-3 font-medium">No spare game plays recorded yet.</p>
        <p className="text-sm mt-1 opacity-60">
          Spare plays appear here once a spare checks in to a game and track &amp; invoice mode is active in Team Settings.
        </p>
      </div>
    );
  }

  const STATUS_COLORS = {
    unpaid:   { bg: 'rgba(255,68,68,0.1)',    color: 'var(--penalty)', label: 'Unpaid' },
    paid:     { bg: 'rgba(0,255,136,0.1)',    color: 'var(--goal)',    label: 'Paid ✓' },
    waived:   { bg: 'rgba(0,212,255,0.1)',    color: 'var(--ice)',     label: 'Waived' },
    invoiced: { bg: 'rgba(167,139,250,0.1)',  color: '#a78bfa',       label: 'Invoiced' },
  };

  const totalOwed = plays.reduce((s, p) => s + (p.totalOwed || 0), 0);
  const totalPaid = plays.reduce((s, p) => s + (p.totalPaid || 0), 0);

  return (
    <div className="space-y-4">
      {/* v2.5.149 — scope hint when the auto-fallback fired so the
          coach knows we showed all-seasons because the active-season
          slice was empty. Helps explain why a freshly-switched
          season looks the same as before. */}
      {scope?.fellBack && (
        <div className="glass rounded-xl p-3 text-xs" style={{ borderLeft: '3px solid var(--ice)' }}>
          📅 No spare plays in the active season — showing all seasons instead.
        </div>
      )}

      {/* v2.5.153 — Export. Flattens each play into its own row
          (spare name + game + amount + status), which is more useful
          for invoicing than a per-spare summary. */}
      <div className="flex justify-end">
        <ExportMenu
          filename="spare-plays"
          title="Spare Plays"
          columns={[
            { key: 'name',     label: 'Spare' },
            { key: 'email',    label: 'Email' },
            { key: 'playedAt', label: 'Date' },
            { key: 'fee',      label: 'Fee', align: 'right' },
            { key: 'status',   label: 'Status' },
          ]}
          getRows={() => {
            const out = [];
            for (const spare of plays) {
              for (const play of (spare.plays || [])) {
                const d = play.playedAt ? new Date(play.playedAt) : null;
                out.push({
                  name:     spare.name,
                  email:    spare.email || '',
                  playedAt: d ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '',
                  fee:      `$${((play.feeCents || 0) / 100).toFixed(2)}`,
                  status:   play.status || 'unpaid',
                });
              }
            }
            return out;
          }}
        />
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Spares tracked', val: plays.length, color: 'var(--ice)' },
          { label: 'Total owed',  val: `$${(totalOwed / 100).toFixed(2)}`,  color: 'var(--penalty)' },
          { label: 'Total paid',  val: `$${(totalPaid / 100).toFixed(2)}`,  color: 'var(--goal)' },
        ].map(s => (
          <div key={s.label} className="glass rounded-xl p-3 text-center">
            <div className="text-xl font-bold" style={{ color: s.color }}>{s.val}</div>
            <div className="text-xs text-secondary mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Per-spare breakdown */}
      {plays.map(spare => {
        const outstanding = (spare.totalOwed - spare.totalPaid) / 100;
        return (
          <div key={spare.userId} className="card">
            <div className="flex items-center justify-between mb-3" style={{ gap: 8, flexWrap: 'wrap' }}>
              {/* Mobile fix: minWidth 0 + wordBreak so a long email with no
                  spaces (common) wraps instead of forcing the card to
                  scroll horizontally on ~390px screens. */}
              <div style={{ minWidth: 0 }}>
                <p className="font-semibold text-sm">{spare.name}</p>
                <p className="text-xs text-secondary" style={{ overflowWrap: 'anywhere' }}>{spare.email}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold" style={{ color: outstanding > 0 ? 'var(--penalty)' : 'var(--goal)' }}>
                  {outstanding > 0 ? `-$${outstanding.toFixed(2)} owed` : '✓ Settled'}
                </p>
                <p className="text-xs text-secondary">{spare.gamesPlayed} game{spare.gamesPlayed !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="space-y-1.5">
              {(spare.plays || []).map(play => {
                const sc = STATUS_COLORS[play.status] || STATUS_COLORS.unpaid;
                return (
                  <div key={play.gameId} className="flex items-center gap-2 px-2 py-1.5 rounded-lg"
                    style={{ background: 'var(--surface-2)' }}>
                    <span className="text-xs text-secondary flex-1">
                      {play.playedAt ? format(parseISO(play.playedAt), 'MMM d, yyyy') : '—'}
                    </span>
                    {play.feeCents && (
                      <span className="text-xs font-semibold">${(play.feeCents / 100).toFixed(2)}</span>
                    )}
                    <select
                      value={play.status || 'unpaid'}
                      disabled={updating[play.gameId]}
                      onChange={e => updatePayment({ ...play, playId: play.id }, e.target.value)}
                      className="text-xs rounded-lg px-2 py-1 font-semibold border-0 outline-none cursor-pointer"
                      style={{ background: sc.bg, color: sc.color }}>
                      {Object.entries(STATUS_COLORS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Player Log tab ────────────────────────────────────────────────────────────
// Combined per-player breakdown: name + FT/Spare classification + games-played
// count, plus a $ owed column when the team has enabled the track-and-invoice
// spare payment mode. Coach/manager only — route-guarded above.
function PlayerLogTab({ teamId, seasonParam = 'all' }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  // Sort controls. Default: full-timers first, then by games-played desc.
  // Keys match the response payload exactly.
  const [sortKey, setSortKey] = useState('default');
  // v2.5.153 — Type filter (All / Full-Time / Spares). Coaches asked for
  // quick filtering so the log can answer "how many games did each spare
  // play?" and "how many did each FT play?" separately. The filter
  // applies BEFORE the summary totals + table render + export, so an
  // exported file matches what's on screen.
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'ft' | 'spare'

  // Drill-down: clicking a player name opens a per-player timeline
  // modal (RSVP / waitlist / promotion / roster / spare-play history).
  const [drillUser,    setDrillUser]    = useState(null); // { userId, name } | null
  const [drillData,    setDrillData]    = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  useEffect(() => {
    api.get(`/teams/${teamId}/stats/player-log`, { params: { seasonId: seasonParam } })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load player log'))
      .finally(() => setLoading(false));
  }, [teamId, seasonParam]);

  // Fetch the selected player's timeline. Cancellation guards against a
  // fast switch between two players.
  useEffect(() => {
    if (!drillUser) { setDrillData(null); return; }
    let cancelled = false;
    setDrillLoading(true);
    setDrillData(null);
    api.get(`/teams/${teamId}/stats/player/${drillUser.userId}/timeline`, { params: { seasonId: seasonParam } })
      .then(r => { if (!cancelled) setDrillData(r.data); })
      .catch(err => { if (!cancelled) toast.error(err.response?.data?.error || 'Failed to load player timeline'); })
      .finally(() => { if (!cancelled) setDrillLoading(false); });
    return () => { cancelled = true; };
  }, [drillUser, teamId, seasonParam]);

  if (loading) return <PageSpinner />;
  if (!data) return null;

  const { players, currency, canInvoice, spareFeeCents } = data;

  // Sort variants. "default" = active first → FT first → games-played desc.
  // Explicit keys ("games", "owed", "name") flip to single-criterion sorts
  // so the coach can find the largest invoiceable spare or the most active
  // player at a glance. Deleted rows always sink to the bottom.
  const filtered = players.filter(p => {
    if (typeFilter === 'ft')    return p.isFullTime;
    if (typeFilter === 'spare') return !p.isFullTime;
    return true;
  });
  const sorted = [...filtered].sort((a, b) => {
    const aDel = a.isDeleted ? 1 : 0, bDel = b.isDeleted ? 1 : 0;
    if (aDel !== bDel) return aDel - bDel;
    if (sortKey === 'games') return b.gamesPlayed - a.gamesPlayed;
    if (sortKey === 'owed')  return (b.spareOwedCents || 0) - (a.spareOwedCents || 0);
    if (sortKey === 'name')  return (a.name || '').localeCompare(b.name || '');
    // default
    const aFT = a.isFullTime ? 0 : 1, bFT = b.isFullTime ? 0 : 1;
    if (aFT !== bFT) return aFT - bFT;
    return b.gamesPlayed - a.gamesPlayed;
  });

  const money = (cents) => {
    const amount = (cents || 0) / 100;
    const upper = (currency || 'cad').toUpperCase();
    return `${upper === 'USD' ? '$' : upper === 'CAD' ? '$' : upper === 'EUR' ? '€' : upper === 'GBP' ? '£' : '$'}${amount.toFixed(2)}`;
  };

  // v2.5.153 — replaced the bespoke CSV export with a shared ExportMenu
  // that produces CSV / Excel / PDF from the same column spec. getRows
  // is evaluated at click-time so the export always reflects the current
  // sort + type filter.
  const exportColumns = canInvoice
    ? [
        { key: 'name',         label: 'Name'         },
        { key: 'alias',        label: 'Alias'        },
        { key: 'type',         label: 'Type'         },
        { key: 'position',     label: 'Position'     },
        { key: 'gamesPlayed',  label: 'Games Played', align: 'right' },
        { key: 'sparePlays',   label: 'Spare Plays',  align: 'right' },
        { key: 'owed',         label: 'Owed',         align: 'right' },
        { key: 'paid',         label: 'Paid',         align: 'right' },
      ]
    : [
        { key: 'name',         label: 'Name'         },
        { key: 'alias',        label: 'Alias'        },
        { key: 'type',         label: 'Type'         },
        { key: 'position',     label: 'Position'     },
        { key: 'gamesPlayed',  label: 'Games Played', align: 'right' },
      ];
  const getExportRows = () => sorted.map(p => {
    const name = p.isDeleted ? `${p.name} (deleted)` : p.name;
    const type = p.isFullTime ? 'Full-Time' : 'Spare';
    const pos  = p.isGoalie ? 'Goalie' : (p.position || '—');
    const base = { name, alias: p.alias || '', type, position: pos, gamesPlayed: p.gamesPlayed };
    if (canInvoice) {
      return { ...base,
        sparePlays: p.sparePlaysCount,
        owed: money(p.spareOwedCents),
        paid: money(p.sparePaidCents),
      };
    }
    return base;
  });
  const exportTitle = `Player Log — ${typeFilter === 'ft' ? 'Full-Time only' : typeFilter === 'spare' ? 'Spares only' : 'All players'}`;

  const totalOwed = canInvoice ? sorted.reduce((sum, p) => sum + (p.spareOwedCents || 0), 0) : 0;
  const totalPaid = canInvoice ? sorted.reduce((sum, p) => sum + (p.sparePaidCents || 0), 0) : 0;

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2">📇 Player Log</h3>
            <p className="text-xs text-secondary mt-0.5">
              Every player on the roster with their classification and games-played count.
              {canInvoice && ` Spare-fee tracking is on at ${money(spareFeeCents)}/game.`}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {/* v2.5.153 — Type filter chips. Applies to the table,
                summary totals, AND the export, so what you see is what
                gets exported. */}
            <div role="group" aria-label="Filter by player type"
              style={{
                display: 'inline-flex', borderRadius: 8,
                border: '1px solid var(--border)', overflow: 'hidden',
              }}>
              {[
                { key: 'all',   label: 'All' },
                { key: 'ft',    label: 'Full-Time' },
                { key: 'spare', label: 'Spares' },
              ].map(opt => {
                const active = typeFilter === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setTypeFilter(opt.key)}
                    aria-pressed={active}
                    className="text-xs font-semibold"
                    style={{
                      padding: '6px 12px',
                      background: active ? 'var(--ice)' : 'transparent',
                      color: active ? 'var(--btn-accent-text)' : 'var(--text-2)',
                      border: 'none',
                      cursor: 'pointer',
                    }}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <select className="input-field py-1.5 text-xs" style={{ width: 'auto' }}
              value={sortKey} onChange={e => setSortKey(e.target.value)}>
              <option value="default">Sort: Default (FT first)</option>
              <option value="games">Sort: Games Played</option>
              {canInvoice && <option value="owed">Sort: Amount Owed</option>}
              <option value="name">Sort: Name (A–Z)</option>
            </select>
            <ExportMenu
              filename="player-log"
              title={exportTitle}
              columns={exportColumns}
              getRows={getExportRows}
            />
          </div>
        </div>

        {canInvoice && (totalOwed > 0 || totalPaid > 0) && (
          <div className="mb-3 p-3 rounded-xl flex flex-wrap gap-4"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
            <div>
              <div className="text-xs text-secondary">Total Owed</div>
              <div className="text-lg font-bold" style={{ color: 'var(--penalty)' }}>{money(totalOwed)}</div>
            </div>
            <div>
              <div className="text-xs text-secondary">Total Paid</div>
              <div className="text-lg font-bold" style={{ color: 'var(--goal)' }}>{money(totalPaid)}</div>
            </div>
            <div>
              <div className="text-xs text-secondary">Players</div>
              <div className="text-lg font-bold">{sorted.length}</div>
            </div>
          </div>
        )}

        {sorted.length === 0 ? (
          <p className="text-sm text-secondary text-center py-4">No players on this team yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th className="text-left py-2 px-2 font-semibold text-xs uppercase tracking-wide" style={{ color: 'var(--text-2)' }}>Player</th>
                  <th className="text-left py-2 px-2 font-semibold text-xs uppercase tracking-wide" style={{ color: 'var(--text-2)' }}>Type</th>
                  <th className="text-right py-2 px-2 font-semibold text-xs uppercase tracking-wide" style={{ color: 'var(--text-2)' }}>Games</th>
                  {canInvoice && <>
                    <th className="text-right py-2 px-2 font-semibold text-xs uppercase tracking-wide" style={{ color: 'var(--text-2)' }}>Spare Plays</th>
                    <th className="text-right py-2 px-2 font-semibold text-xs uppercase tracking-wide" style={{ color: 'var(--text-2)' }}>Owed</th>
                    <th className="text-right py-2 px-2 font-semibold text-xs uppercase tracking-wide" style={{ color: 'var(--text-2)' }}>Paid</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {/* 2026-07-10 (batch-B A5, coach bug) — key was
                    `p.userId || p.name`: placeholder/unclaimed rows have
                    no userId AND share the fallback name '—', so keys
                    COLLIDED. Toggling the Spares → Full-Time filter then
                    left a stale, unreconciled row (rendered with the
                    'Spare' type badge and a blank name) at the top.
                    Index fallback guarantees uniqueness within a render. */}
                {sorted.map((p, rowIdx) => (
                  <tr key={p.userId || `row-${rowIdx}`} style={{
                    borderBottom: '1px solid var(--border)',
                    opacity: p.isDeleted ? 0.5 : 1,
                  }}>
                    <td className="py-2.5 px-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Clickable — opens the per-player timeline drill-down.
                            Unclaimed/placeholder players (no userId) aren't
                            clickable since the timeline keys off a real user. */}
                        {p.userId ? (
                          <button
                            type="button"
                            onClick={() => setDrillUser({ userId: p.userId, name: p.name })}
                            className="font-semibold"
                            title="View this player's full check-in / waitlist / roster history"
                            style={{
                              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                              color: 'var(--ice)', textDecoration: 'underline', textUnderlineOffset: 2,
                            }}>
                            {p.name}
                          </button>
                        ) : (
                          <span className="font-semibold">{p.name}</span>
                        )}
                        {p.alias && (
                          <span className="text-xs italic" style={{ color: 'var(--text-3)' }}
                            title={`Other players see this person as "${p.alias}"`}>
                            (aka {p.alias})
                          </span>
                        )}
                        {p.isDeleted && (
                          <span className="text-xs" style={{ color: 'var(--penalty)' }}>🗑 deleted</span>
                        )}
                        {p.isGoalie && (
                          <span className="text-xs">🥅</span>
                        )}
                        {['coach','manager'].includes(p.memberRole) && (
                          <span className="text-xs font-semibold" style={{ color: 'var(--ice)' }}>
                            {/* Apr 30 2026 (App_Change_List #1) — both
                                coach and legacy manager render as Coach
                                in the user-facing UI. */}
                            👑 Coach
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-2">
                      <span className="text-xs font-bold px-2 py-0.5 rounded" style={{
                        background: p.isFullTime ? 'rgba(0,212,255,0.12)' : 'rgba(167,139,250,0.12)',
                        color:      p.isFullTime ? 'var(--ice)'            : '#a78bfa',
                        border:    `1px solid ${p.isFullTime ? 'var(--ice-a30)' : 'rgba(167,139,250,0.3)'}`,
                      }}>
                        {p.isFullTime ? 'Full-Time' : 'Spare'}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-right font-bold">{p.gamesPlayed}</td>
                    {canInvoice && <>
                      <td className="py-2.5 px-2 text-right">{p.sparePlaysCount}</td>
                      <td className="py-2.5 px-2 text-right font-bold" style={{
                        color: (p.spareOwedCents || 0) > 0 ? 'var(--penalty)' : 'var(--text-3)',
                      }}>
                        {money(p.spareOwedCents)}
                      </td>
                      <td className="py-2.5 px-2 text-right" style={{
                        color: (p.sparePaidCents || 0) > 0 ? 'var(--goal)' : 'var(--text-3)',
                      }}>
                        {money(p.sparePaidCents)}
                      </td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!canInvoice && (
          <p className="text-xs text-secondary mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
            💡 To see per-spare amounts owed here, enable Payments in Team Settings — or if you collect offline, contact support to enable end-of-season invoice tracking on your team.
          </p>
        )}
      </div>

      {drillUser && (
        <PlayerTimelineModal
          user={drillUser}
          data={drillData}
          loading={drillLoading}
          onClose={() => setDrillUser(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PlayerTimelineModal — per-player drill-down for the Player Log.
// Shows, per game, the full lifecycle: RSVP/check-in, spare status,
// waitlist placement + coach priority bump, coach promotion, roster
// assignment (home/away, coach-adjusted), and spare play + payment.
// ─────────────────────────────────────────────────────────────
function PlayerTimelineModal({ user, data, loading, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fmtDateTime = (v) => {
    if (!v) return '—';
    try { return format(parseISO(v), 'MMM d, yyyy · h:mm a'); } catch { return '—'; }
  };
  const fmtGameDate = (v) => {
    if (!v) return '—';
    try { return format(parseISO(String(v).slice(0, 10)), 'EEE MMM d, yyyy'); } catch { return '—'; }
  };
  const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

  // RSVP status → readable + color.
  const rsvpLabel = (s) => ({
    in: "Checked in (In)", out: 'Out', maybe: 'Maybe',
    waitlist: 'Waitlisted', pending: 'Pending', pending_payment: 'Pending payment',
  }[s] || s || '—');
  const rsvpColor = (s) => {
    if (s === 'in') return 'var(--goal)';
    if (s === 'out') return 'var(--penalty)';
    if (s === 'waitlist' || s === 'pending' || s === 'pending_payment') return 'var(--puck)';
    return 'var(--text-2)';
  };

  // One timeline event → a single readable line.
  const renderEvent = (ev, i) => {
    let icon = '•', text = '', sub = '';
    if (ev.type === 'rsvp') {
      icon = ev.status === 'in' ? '✅' : ev.status === 'out' ? '🚫' : ev.status === 'waitlist' ? '⏳' : '✉️';
      text = `RSVP: ${rsvpLabel(ev.status)}${ev.isSpare ? ' (as spare)' : ''}`;
      if (ev.playingAs) sub = `Playing as ${ev.playingAs}`;
      if (ev.notes) sub = sub ? `${sub} · “${ev.notes}”` : `“${ev.notes}”`;
    } else if (ev.type === 'waitlist_priority') {
      icon = '⭐'; text = 'Coach set waitlist priority (moved up the list)';
    } else if (ev.type === 'promoted') {
      icon = '⬆️'; text = 'Promoted from waitlist by coach';
    } else if (ev.type === 'roster') {
      icon = '🏒';
      text = `Placed on ${ev.teamAssignment === 'home' ? 'Home' : ev.teamAssignment === 'away' ? 'Away' : ev.teamAssignment} roster`;
      if (ev.wasAdjusted) sub = 'Coach manually adjusted the assignment';
    } else if (ev.type === 'spare_play') {
      icon = '💸';
      const st = ev.paymentStatus ? ev.paymentStatus.charAt(0).toUpperCase() + ev.paymentStatus.slice(1) : '—';
      text = `Spare play recorded — ${money(ev.feeCents)} (${st})`;
      if (ev.note) sub = ev.note;
    }
    return (
      <div key={i} className="flex items-start gap-2" style={{ padding: '4px 0' }}>
        <span style={{ fontSize: 13, lineHeight: '18px' }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div className="text-sm">{text}</div>
          {sub && <div className="text-xs" style={{ color: 'var(--text-3)' }}>{sub}</div>}
          <div className="text-xs" style={{ color: 'var(--text-3)' }}>{fmtDateTime(ev.at)}</div>
        </div>
      </div>
    );
  };

  const player = data?.player;
  const games  = data?.games || [];
  const summary = data?.summary;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--border)',
          width: '100%', maxWidth: 760,
          // Mobile fix: bare vh doesn't account for the Capacitor
          // native keyboard/viewport; --mobile-h (set by
          // useCapacitorPlugins) does, matching the pattern used
          // elsewhere (e.g. finance/SparesTab.jsx).
          maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)',
          display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
        }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid var(--border)',
        }}>
          <div>
            <h2 className="font-bold text-base">{player?.name || user.name}</h2>
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>
              {player ? (
                <>
                  {player.isFullTime ? 'Full-Time' : 'Spare'}
                  {player.isGoalie ? ' · Goalie' : ''}
                  {player.email ? ` · ${player.email}` : ''}
                </>
              ) : 'Loading…'}
            </p>
          </div>
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{
              background: 'transparent', border: '1px solid var(--border)', borderRadius: 8,
              padding: '6px 10px', cursor: 'pointer', color: 'var(--text-2)', fontSize: 14,
            }}>✕</button>
        </div>

        {/* Summary row */}
        {!loading && summary && (
          <div style={{
            display: 'flex', gap: 16, padding: '12px 18px',
            borderBottom: '1px solid var(--border)', flexWrap: 'wrap',
          }}>
            <SummaryStat label="Games" value={summary.gamesInteracted} />
            <SummaryStat label="Checked in" value={summary.gamesIn} color="var(--goal)" />
            <SummaryStat label="Out" value={summary.gamesOut} color="var(--penalty)" />
            <SummaryStat label="Waitlisted" value={summary.gamesWaitlist} color="var(--puck)" />
            <SummaryStat label="Promoted" value={summary.promotedCount} color="var(--ice)" />
            <SummaryStat label="Spare plays" value={summary.sparePlays} />
          </div>
        )}

        {/* Body */}
        <div style={{ overflow: 'auto', padding: '12px 18px 18px' }}>
          {loading && <p className="text-sm text-secondary py-6 text-center">Loading timeline…</p>}
          {!loading && games.length === 0 && (
            <p className="text-sm text-secondary py-6 text-center">
              No check-in, roster, or spare history for this player{' '}
              in the selected season.
            </p>
          )}
          {!loading && games.map(g => (
            <div key={g.gameId} className="card" style={{ marginBottom: 10, padding: '10px 14px', background: 'var(--surface-2)' }}>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <div className="font-semibold text-sm">
                  {fmtGameDate(g.gameDate)}
                  {g.gameTime ? <span style={{ color: 'var(--text-3)', fontWeight: 400 }}> · {String(g.gameTime).slice(0, 5)}</span> : null}
                  {g.arenaName ? <span style={{ color: 'var(--text-3)', fontWeight: 400 }}> · {g.arenaName}</span> : null}
                </div>
                <div className="flex items-center gap-1.5">
                  {g.rsvpStatus && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded"
                      style={{ color: rsvpColor(g.rsvpStatus), border: `1px solid ${rsvpColor(g.rsvpStatus)}`, opacity: 0.9 }}>
                      {rsvpLabel(g.rsvpStatus)}
                    </span>
                  )}
                  {g.isSpare && (
                    <span className="text-xs px-2 py-0.5 rounded" style={{ color: '#a78bfa', border: '1px solid rgba(167,139,250,0.4)' }}>Spare</span>
                  )}
                </div>
              </div>
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 4 }}>
                {g.events.length === 0
                  ? <div className="text-xs" style={{ color: 'var(--text-3)' }}>No recorded events.</div>
                  : g.events.map((ev, i) => renderEvent(ev, i))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SummaryStat({ label, value, color }) {
  return (
    <div>
      <div className="text-xs" style={{ color: 'var(--text-3)' }}>{label}</div>
      <div className="font-bold" style={{ fontSize: 16, color: color || 'var(--text)' }}>{value ?? 0}</div>
    </div>
  );
}
